<template>
  <div class="accreditation-order-page">
    <v-layout row wrap>
      <v-flex xs12>
        <div v-if="loadError" class="order-error">
          <button type="button" class="order-back-button" @click="goBack">
            <v-icon size="20" color="#0277bd">arrow_back</v-icon>
            <span>{{ workflowLabel(order ? adminText('workflowsAll.order_details_1a89794', 'Order details') : adminText('workflowsAll.accreditation_details_b95ff7d', 'Accreditation details')) }}</span>
          </button>
          <section class="order-card">
            <div class="card-body">
              {{ workflowLabel(loadError) }}
            </div>
          </section>
        </div>
        <div v-else-if="!event || !currentAttendee" class="order-loading">
          <span class="page-title">{{ adminText('workflowsAll.loading_b04ba49', 'Loading...') }}</span>
        </div>
        <div v-else>
          <button type="button" class="order-back-button" @click="goBack">
            <v-icon size="20" color="#0277bd">arrow_back</v-icon>
            <span>{{ workflowLabel(order ? adminText('workflowsAll.order_details_1a89794', 'Order details') : adminText('workflowsAll.accreditation_details_b95ff7d', 'Accreditation details')) }}</span>
          </button>

          <accreditation-details-layout>
            <template slot="main">
              <section v-if="order" class="order-card buyer-card">
                <header class="card-heading">
                  <h1>{{ adminText('workflowsAll.order_100_ed40022', 'Order #100') }}{{ workflowLabel(order.id) }}</h1>
                  <p>{{ adminText('workflowsAll.bought_online_b15ee82', 'Bought online') }} {{ workflowLabel(formatDateTime(order.createdAt)) }}</p>
                </header>
                <div class="card-body">
                  <div class="detail-row">
                    <span>{{ adminText('workflowsAll.ticket_buyer_a81c2f5', 'Ticket Buyer') }}</span>
                    <strong>{{ workflowLabel(buyerName) }}</strong>
                  </div>
                  <div class="detail-row detail-row-action">
                    <div>
                      <span>{{ adminText('workflowsAll.buyer_email_1a764f2', 'Buyer Email') }}</span>
                      <strong>{{ workflowLabel(buyerEmail || '-') }}</strong>
                    </div>
                    <button
                      type="button"
                      class="text-action"
                      :disabled="!buyerEmail || actionLoading"
                      @click="resendOrderTickets"
                    >
                      <v-icon size="20">mail_outline</v-icon>
                      <span>{{ adminText('workflowsAll.resend_0f4f469', 'Resend') }}</span>
                    </button>
                  </div>
                </div>
              </section>

              <section v-if="hasTicketPersonalization" class="ticket-owners-section">
                <div class="section-title-row">
                  <h2>{{ adminText('workflowsAll.ticket_owners_8b115f2', 'Ticket owners') }}</h2>
                  <button
                    v-if="!editingOwners"
                    type="button"
                    class="text-action edit-owners-action"
                    @click="startEditingOwners"
                  >
                    <v-icon size="20">edit</v-icon>
                    <span>{{ adminText('workflowsAll.edit_owners_info_61fdf92', 'Edit owners info') }}</span>
                  </button>
                </div>

                <div v-if="editingOwners" class="owner-list">
                  <article
                    v-for="(purchase, index) in orderPurchases"
                    :key="`edit-${purchase.id}`"
                    class="owner-edit-card"
                  >
                    <button type="button" class="owner-edit-header" @click="toggleOwner(index)">
                      <v-icon size="22">assignment_ind</v-icon>
                      <strong>{{ workflowLabel(ownerEditTitle(purchase, index)) }}</strong>
                      <v-icon size="20">
                        {{ expandedOwnerIndex === index ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
                      </v-icon>
                    </button>

                    <div v-show="expandedOwnerIndex === index" class="owner-edit-body">
                      <label v-if="index === 0" class="same-as-buyer-row">
                        <input
                          v-model="ownerForms[index].sameAsBuyer"
                          type="checkbox"
                          @change="syncOwnerWithBuyer(index)"
                        >
                        <span>{{ adminText('workflowsAll.the_owner_s_information_is_the_same_as_the_bu_0cff0c8', 'The owner’s information is the same as the buyer’s information') }}</span>
                      </label>

                      <v-text-field
                        v-for="field in editableOwnerFields"
                        :key="`${purchase.id}-${field.key}`"
                        v-model="ownerForms[index].fields[field.key]"
                        :label="workflowLabel(ownerFieldLabel(field))"
                        :error-messages="ownerFieldErrorMessages(index, field)"
                        :disabled="index === 0 && ownerForms[index].sameAsBuyer && isBuyerField(field.key)"
                        outline
                        @blur="touchOwnerField(index, field.key)"
                        @input="touchOwnerField(index, field.key)"
                      />
                    </div>
                  </article>

                  <div class="owner-edit-actions">
                    <main-button flat color="#666666" @click.prevent="cancelOwnersEdit">
                      {{ adminText('workflowsAll.cancel_77dfd21', 'Cancel') }}
                    </main-button>
                    <main-button depressed :loading="actionLoading" @click.prevent="saveOwnerForms">
                      {{ adminText('workflowsAll.update_fb91e24', 'Update') }}
                    </main-button>
                  </div>
                </div>

                <div v-else class="owner-list">
                  <article
                    v-for="(purchase, index) in orderPurchases"
                    :key="purchase.id"
                    class="owner-card"
                  >
                    <button type="button" class="owner-card-header" @click="toggleOwner(index)">
                      <span>
                        <strong>{{ workflowLabel(ownerName(purchase) || (adminText('workflowsAll.ticket_owner_0899669', 'Ticket owner #') + (index + 1) + '')) }}</strong>
                        <small>{{ workflowLabel(purchase.orderItem && purchase.orderItem.name) }}</small>
                      </span>
                      <v-icon size="20">
                        {{ expandedOwnerIndex === index ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
                      </v-icon>
                    </button>

                    <div v-show="expandedOwnerIndex === index" class="owner-card-body">
                      <div class="owner-details">
                        <div
                          v-for="field in ownerDisplayFields(purchase)"
                          :key="`${purchase.id}-${field.label}`"
                          class="detail-row"
                        >
                          <span>{{ workflowLabel(field.label) }}</span>
                          <strong>{{ workflowLabel(field.value || '-') }}</strong>
                          <button
                            v-if="field.key === 'Email'"
                            type="button"
                            class="text-action owner-resend"
                            :disabled="!field.value || actionLoading"
                            @click="resendOwnerTicket(purchase)"
                          >
                            <v-icon size="20">mail_outline</v-icon>
                            <span>{{ adminText('workflowsAll.resend_0f4f469', 'Resend') }}</span>
                          </button>
                        </div>
                      </div>

                      <div class="owner-actions">
                        <button
                          type="button"
                          class="pill-button pill-button-primary"
                          :disabled="actionLoading"
                          @click="downloadOwnerTicket(purchase)"
                        >
                          <v-icon size="20" color="#ffffff">confirmation_number</v-icon>
                          <span>{{ adminText('workflowsAll.download_ticket_5974d30', 'Download ticket') }}</span>
                        </button>
                        <button
                          type="button"
                          class="pill-button pill-button-outline"
                          :disabled="!designSet || actionLoading"
                          @click="printAccreditation(purchase.accreditation.uuid)"
                        >
                          <v-icon size="20">assignment_ind</v-icon>
                          <span>{{ adminText('workflowsAll.print_accreditation_cf80d0e', 'Print accreditation') }}</span>
                        </button>
                      </div>
                    </div>
                  </article>
                </div>
              </section>

              <section v-else class="order-card accreditation-info-card">
                <header class="plain-card-heading">
                  <h2>{{ adminText('workflowsAll.accreditation_info_5adbd71', 'Accreditation info') }}</h2>
                </header>
                <form v-scroll-to-error class="accreditation-form-card" @submit.prevent="submitForm">
                  <accreditation-form
                    :key="formRenderKey"
                    :event="event"
                    :initial-values="form"
                    :public-form="false"
                    :vs="$v"
                    :styles="customStyles.attendeeForm && customStyles.attendeeForm.form"
                    :fields="schema.attendeeFields"
                    @values="form = $event"
                  />
                  <div v-if="event.flags && event.flags.timeRestrictedAttendeeEntrance" class="date-row">
                    <datetime-field
                      v-model="form.entryFrom"
                      :label="adminText('workflowsAll.entry_from_2ea1c29', 'Entry from*')"
                      :max="event.to.toString()"
                    />
                    <datetime-field
                      v-model="form.entryTo"
                      :min="event.from.toString()"
                      :label="adminText('workflowsAll.entry_to_f6ccf63', 'Entry to*')"
                    />
                  </div>
                  <div class="form-actions">
                    <main-button
                      flat
                      color="#666666"
                      :disabled="!hasFormChanges || actionLoading"
                      @click.prevent="cancelFormChanges"
                    >
                      {{ adminText('workflowsAll.cancel_77dfd21', 'Cancel') }}
                    </main-button>
                    <main-button
                      depressed
                      :disabled="!hasFormChanges || actionLoading"
                      :loading="actionLoading"
                      @click.prevent="submitForm"
                    >
                      {{ adminText('workflowsAll.update_fb91e24', 'Update') }}
                    </main-button>
                  </div>
                </form>
              </section>
            </template>

            <template slot="sidebar">
              <template v-if="order">
                <section class="order-card summary-card">
                  <header class="card-heading">
                    <h2>{{ workflowLabel(orderEvent.title) }}</h2>
                    <p>{{ workflowLabel(formatEventDate(orderEvent)) }}</p>
                  </header>
                  <div class="card-body summary-body">
                    <div v-for="item in groupedOrderItems" :key="item.id" class="summary-row">
                      <span>{{ workflowLabel(item.count) }} x {{ workflowLabel(item.name) }}</span>
                      <strong>{{ workflowLabel(formatMoney(item.total, item.currency)) }}</strong>
                    </div>
                    <hr>
                    <div class="summary-row">
                      <span>{{ adminText('workflowsAll.subtotal_97f7359', 'Subtotal') }}</span>
                      <strong>{{ workflowLabel(formatMoney(orderSubtotal, orderCurrency)) }}</strong>
                    </div>
                    <div class="summary-total">
                      <span>{{ adminText('workflowsAll.total_b25928c', 'Total') }}</span>
                      <strong>{{ workflowLabel(formatMoney(orderTotal, orderCurrency)) }}</strong>
                      <small v-if="hasEventVAT">{{ adminText('workflowsAll.vat_included_in_the_price_32ef420', 'VAT included in the price') }}</small>
                    </div>
                  </div>
                  <footer class="summary-actions">
                    <button
                      type="button"
                      class="pill-button pill-button-primary"
                      :disabled="actionLoading"
                      @click="downloadOrderTickets"
                    >
                      <v-icon size="20" color="#ffffff">confirmation_number</v-icon>
                      <span>{{ adminText('workflowsAll.download_tickets_a7e03fc', 'Download tickets (') }}{{ workflowLabel(ticketCount) }})</span>
                    </button>
                    <button
                      type="button"
                      class="pill-button pill-button-light"
                      :disabled="!designSet || actionLoading"
                      @click="printAllAccreditations"
                    >
                      <v-icon size="20" color="#0277bd">assignment_ind</v-icon>
                      <span>{{ adminText('workflowsAll.print_accreditations_d690894', 'Print accreditations (') }}{{ workflowLabel(ticketCount) }})</span>
                    </button>
                    <button
                      v-if="isOrderRefundAllowed"
                      type="button"
                      class="pill-button pill-button-alert-outline"
                      :disabled="!canCancelOrder || actionLoading"
                      @click="isVisibleCancelOrderDialog = true"
                    >
                      <span>{{ adminText('workflowsAll.cancel_order_5ae39c4', 'Cancel order') }}</span>
                    </button>
                  </footer>
                </section>

                <section class="order-card print-history-card">
                  <button
                    type="button"
                    class="history-header"
                    @click="printHistoryOpen = !printHistoryOpen"
                  >
                    <h2>{{ adminText('workflowsAll.print_history_c67fc1a', 'Print history') }}</h2>
                    <v-icon size="20">
                      {{ printHistoryOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
                    </v-icon>
                  </button>
                  <div v-show="printHistoryOpen" class="history-list">
                    <div v-if="!printHistory.length" class="empty-history">{{ adminText('workflowsAll.no_prints_yet_ed9b462', 'No prints yet.') }}</div>
                    <div v-for="print in printHistory" :key="print.key" class="history-row">
                      <span>
                        <small>{{ workflowLabel(formatPrintDate(print.createdAt)) }}</small>
                        <strong>{{ workflowLabel(print.userName) }}</strong>
                      </span>
                      <span class="history-target">
                        <strong>{{ workflowLabel(print.ownerName) }}</strong>
                        <v-icon size="20">assignment_ind</v-icon>
                      </span>
                    </div>
                  </div>
                </section>
              </template>
              <template v-else>
                <section class="order-card print-history-card">
                  <button type="button" class="history-header" @click="printHistoryOpen = !printHistoryOpen">
                    <h2>{{ adminText('workflowsAll.print_history_c67fc1a', 'Print history') }}</h2>
                    <v-icon size="20">
                      {{ printHistoryOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
                    </v-icon>
                  </button>
                  <div v-show="printHistoryOpen" class="history-list">
                    <div v-if="!accreditationPrintHistory.length" class="empty-history">{{ adminText('workflowsAll.no_prints_yet_ed9b462', 'No prints yet.') }}</div>
                    <div v-for="print in accreditationPrintHistory" :key="print.key" class="history-row">
                      <span>
                        <small>{{ workflowLabel(formatPrintDate(print.createdAt)) }}</small>
                        <strong>{{ workflowLabel(print.userName) }}</strong>
                      </span>
                      <span class="history-target">
                        <strong>{{ workflowLabel(attendeeName) }}</strong>
                        <v-icon size="20">assignment_ind</v-icon>
                      </span>
                    </div>
                  </div>
                </section>
                <section class="order-card accreditation-actions-card">
                  <div class="card-body accreditation-actions">
                    <button
                      type="button"
                      class="pill-button pill-button-accreditation-print"
                      :disabled="!designSet || actionLoading"
                      @click="printAccreditation(currentAttendee.uuid)"
                    >
                      <img
                        class="accreditation-print-icon"
                        src="/assets/print-accreditation.svg"
                        alt=""
                      >
                      <span>{{ adminText('workflowsAll.print_accreditation_cf80d0e', 'Print accreditation') }}</span>
                    </button>
                    <button
                      type="button"
                      class="pill-button pill-button-alert-outline"
                      :disabled="actionLoading"
                      @click="isVisibleDeleteDialog = true"
                    >
                      <span>{{ adminText('workflowsAll.delete_accreditation_2da0b1b', 'Delete accreditation') }}</span>
                    </button>
                  </div>
                </section>
              </template>
            </template>
          </accreditation-details-layout>
        </div>
      </v-flex>
    </v-layout>

    <delete-dialog
      :content="workflowLabel((adminText('workflowsAll.are_you_sure_you_want_to_delete_accreditation_bedaf86', 'Are you sure you want to delete accreditation? This operation cannot be undone.')))"
      :on-delete="() => deleteAttendee(accreditationUuid, eventId)"
      :show.sync="isVisibleDeleteDialog"
      :title="adminText('workflowsAll.delete_accreditation_2da0b1b', 'Delete accreditation')"
    />
    <v-dialog v-model="isVisibleCancelOrderDialog" persistent max-width="500px">
      <div class="cancel-order-dialog">
        <h2>{{ adminText('workflowsAll.refund_warning_587b421', 'Refund warning') }}</h2>
        <div class="cancel-order-dialog-copy">
          <p>{{ adminText('workflowsAll.cancellation_fees_may_apply_for_paid_tickets_e53fc57', 'Cancellation fees may apply for paid tickets.') }}</p>
          <p>{{ adminText('workflowsAll.no_fees_apply_for_free_tickets_6d3802b', 'No fees apply for free tickets.') }}</p>
        </div>
        <div class="cancel-order-dialog-actions">
          <button
            type="button"
            class="cancel-order-dialog-button cancel-order-dialog-button-primary"
            :disabled="actionLoading"
            @click="cancelOrder"
          >
            {{ adminText('workflowsAll.proceed_02ed4a4', 'Proceed') }}
          </button>
          <button
            type="button"
            class="cancel-order-dialog-button cancel-order-dialog-button-secondary"
            :disabled="actionLoading"
            @click="isVisibleCancelOrderDialog = false"
          >
            {{ adminText('workflowsAll.cancel_77dfd21', 'Cancel') }}
          </button>
        </div>
      </div>
    </v-dialog>
    <simple-snackbar
      :open.sync="snackbar.open"
      :message-prop="workflowLabel(snackbar.message)"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex'
import {
  UPDATE_ATTENDEE,
  DELETE_ATTENDEE,
  GET_ACCREDITATION_ORDER_DETAILS,
  SEND_TICKETS_VIA_EMAIL,
  RESEND_TICKET_OWNER_EMAIL,
} from './../../graphql/accreditation.graphql'
import { BULK_CREATE_PRINT } from './../../graphql/print.graphql'
import DeleteDialog from './../shared/dialogs/delete-dialog.vue'
import AccreditationForm from './accreditation-form.vue'
import AccreditationDetailsLayout from './details/AccreditationDetailsLayout.vue'
import SimpleSnackbar from './../shared/snackbars/simple-snackbar.vue'
import { frontendURL, submitGuard, createAttendeeSchema, getEventVAT } from './../../utils'
import MainButton from './../shared/main-button'
import { getAuthorization } from './../../http'

import { email, required, requiredIf } from 'vuelidate/lib/validators'

const DEFAULT_SNACKBAR_TIMEOUT = 4000
// A manual WSPay refund message names an amount and a ShoppingCartID the operator has to act on, so
// it needs to stay on screen long enough to be read and copied.
const ACTION_REQUIRED_SNACKBAR_TIMEOUT = 20000

const PERSONAL_FIELD_KEYS = ['First name', 'Last name', 'Email']
const BUYER_ADDRESS_FIELD_KEYS = [
  'Address',
  'address',
  'City',
  'city',
  'Postal code',
  'Postal Code',
  'postalCode',
  'zipCode',
  'Country',
  'country',
]
const BUYER_FIELD_KEYS = [...PERSONAL_FIELD_KEYS, ...BUYER_ADDRESS_FIELD_KEYS]

export default {
  mixins: [WorkflowDisplay],
  name: 'accreditation-update-container',
  components: {
    AccreditationDetailsLayout,
    AccreditationForm,
    DeleteDialog,
    MainButton,
    SimpleSnackbar,
  },
  props: {
    accreditationUuid: String,
    eventId: String,
    organizationSlug: String,
  },
  data: () => ({
    status: null,
    form: null,
    event: null,
    order: null,
    loadError: '',
    currentAttendee: null,
    initialForm: null,
    formRenderKey: 0,
    isVisibleDeleteDialog: false,
    schema: {},
    customStyles: {},
    editingOwners: false,
    expandedOwnerIndex: 0,
    ownerForms: [],
    printHistoryOpen: true,
    actionLoading: false,
    isVisibleCancelOrderDialog: false,
    organizationRole: null,
    snackbar: {
      open: false,
      message: '',
      color: 'primary',
      timeout: DEFAULT_SNACKBAR_TIMEOUT,
    },
  }),
  validations() {
    const event = this.event || { extraModules: {} }
    const ownerFieldValidationSchema = this.editableOwnerFields.reduce((acc, field) => {
      const schemaValidators =
        (this.schema.validationSchema && this.schema.validationSchema[field.key]) || {}

      if (field.key === 'First name' || field.key === 'Last name') {
        acc[field.key] = { ...schemaValidators, required }
      } else if (field.key === 'Email') {
        acc[field.key] = { ...schemaValidators, required, email }
      } else {
        acc[field.key] = schemaValidators
      }

      return acc
    }, {})

    return {
      form: {
        category: { required: requiredIf(() => event.extraModules.categoriesAndZones) },
        zones: { required: requiredIf(() => event.extraModules.categoriesAndZones) },
        ...this.schema.validationSchema,
      },
      ownerForms: {
        $each: {
          fields: ownerFieldValidationSchema,
        },
      },
    }
  },
  computed: {
    ...mapGetters('auth', ['user']),
    isSuperAdmin() {
      return this.user && this.user.type === 'SUPER_ADMIN'
    },
    isOrganizationOwner() {
      return this.organizationRole === 'OWNER'
    },
    isOrderRefundAllowed() {
      return this.isSuperAdmin || this.isOrganizationOwner
    },
    designSet() {
      return !!(this.event && this.event.builder)
    },
    hasTicketPersonalization() {
      return !!(
        this.orderEvent &&
        this.orderEvent.extraModules &&
        this.orderEvent.extraModules.ticketPersonalization
      )
    },
    orderPurchases() {
      return (this.order && this.order.purchases ? this.order.purchases : []).filter(
        purchase => purchase.accreditation,
      )
    },
    ticketCount() {
      return this.orderPurchases.length
    },
    orderEvent() {
      return (this.order && this.order.event) || this.event || {}
    },
    buyerFields() {
      const buyer = (this.order && this.order.buyer) || {}

      return buyer.fields || buyer || {}
    },
    buyerName() {
      const fields = this.buyerFields
      const firstName = fields.firstName || fields['First name'] || ''
      const lastName = fields.lastName || fields['Last name'] || ''

      return [firstName, lastName].filter(Boolean).join(' ') || fields.name || '-'
    },
    buyerEmail() {
      return this.buyerFields.email || this.buyerFields.Email || ''
    },
    groupedOrderItems() {
      const grouped = this.orderPurchases.reduce((acc, purchase) => {
        const item = purchase.orderItem || {}
        const key = item.id
        const price = Number(item.price || 0)

        if (!acc[key]) {
          acc[key] = {
            id: key,
            name: item.name,
            currency: item.currency || this.orderCurrency,
            count: 0,
            total: 0,
          }
        }
        acc[key].count += 1
        acc[key].total += price

        return acc
      }, {})

      return Object.values(grouped)
    },
    orderCurrency() {
      return (
        (this.groupedOrderItems[0] && this.groupedOrderItems[0].currency) ||
        (this.orderEvent && this.orderEvent.currency) ||
        'EUR'
      )
    },
    orderSubtotal() {
      return this.groupedOrderItems.reduce((sum, item) => sum + item.total, 0)
    },
    orderTotal() {
      if (!this.order || !this.order.price) {
        return this.orderSubtotal
      }
      const parts = String(this.order.price).split(' ')
      const parsed = Number(parts[1] || parts[0])

      return Number.isNaN(parsed) ? this.orderSubtotal : parsed
    },
    hasEventVAT() {
      return getEventVAT(this.orderEvent) > 0
    },
    canCancelOrder() {
      return !!(
        this.isOrderRefundAllowed &&
        this.order &&
        this.order.uuid &&
        this.order.status === 'SUCCESSFUL'
      )
    },
    editableOwnerFields() {
      return (this.schema.attendeeFields || []).filter(field => {
        return (
          !field.isPrivate &&
          field.component !== 'avatar-field' &&
          !['Images', 'Files', 'entryFrom', 'entryTo'].includes(field.key)
        )
      })
    },
    printHistory() {
      return this.orderPurchases
        .reduce((prints, purchase) => {
          const ownerName = this.ownerName(purchase)
          const accreditationPrints =
            (purchase.accreditation && purchase.accreditation.prints) || []

          accreditationPrints.forEach((print, index) => {
            const user = print.user || {}
            prints.push({
              key: `${purchase.id}-${print.id || index}`,
              createdAt: print.createdAt,
              userName: [user.firstName, user.lastName].filter(Boolean).join(' ') || '-',
              ownerName: ownerName || '-',
            })
          })

          return prints
        }, [])
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    },
    accreditationPrintHistory() {
      return ((this.currentAttendee && this.currentAttendee.prints) || [])
        .map((print, index) => {
          const user = print.user || {}

          return {
            key: print.id || index,
            createdAt: print.createdAt,
            userName: [user.firstName, user.lastName].filter(Boolean).join(' ') || '-',
          }
        })
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    },
    attendeeName() {
      const fields = (this.currentAttendee && this.currentAttendee.fields) || {}

      return (
        [this.fieldValue(fields, 'First name'), this.fieldValue(fields, 'Last name')]
          .filter(Boolean)
          .join(' ') || '-'
      )
    },
    hasFormChanges() {
      if (!this.form || !this.initialForm) {
        return false
      }

      return this.normalizeFormValue(this.form) !== this.normalizeFormValue(this.initialForm)
    },
  },
  created() {
    this.loadAuthorization()
  },
  methods: {
    cloneFormValue(value) {
      return value == null ? value : JSON.parse(JSON.stringify(value))
    },
    normalizeFormValue(value) {
      if (Array.isArray(value)) {
        return `[${value.map(item => this.normalizeFormValue(item)).join(',')}]`
      }
      if (value && typeof value === 'object') {
        return `{${Object.keys(value)
          .sort()
          .map(key => `${key}:${this.normalizeFormValue(value[key])}`)
          .join(',')}}`
      }

      return JSON.stringify(value)
    },
    roundMoney(value) {
      return Math.round(Number(value || 0) * 100) / 100
    },
    formatMoney(value, currency = 'EUR') {
      const amount = this.roundMoney(value).toLocaleString('hr-HR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })

      return `${amount} ${currency}`
    },
    formatDateTime(date) {
      return date ? moment(date).format('DD.MM.YYYY., HH:mm [h]') : ''
    },
    formatPrintDate(date) {
      return date ? moment(date).format('DD.MM.YYYY., HH:mm') : ''
    },
    formatEventDate(event) {
      const date = event && event.from ? this.workflowDate(event.from, 'MMMM DD, YYYY [at] HH:mm') : ''
      const location = event && event.location
      const locationText = [location && location.venue, location && location.city].filter(Boolean).join(', ')

      return [date, locationText].filter(Boolean).join(', ')
    },
    ownerFields(purchase) {
      return (purchase.accreditation && purchase.accreditation.fields) || {}
    },
    fieldValue(fields, key) {
      if (key === 'Email') {
        return fields.Email || fields.email || ''
      }
      if (key === 'First name') {
        return fields['First name'] || fields.firstName || ''
      }
      if (key === 'Last name') {
        return fields['Last name'] || fields.lastName || ''
      }

      return fields[key]
    },
    fieldValueAliases(key) {
      const aliases = {
        Address: ['Address', 'address'],
        address: ['address', 'Address'],
        City: ['City', 'city'],
        city: ['city', 'City'],
        'Postal code': ['Postal code', 'Postal Code', 'postalCode', 'zipCode'],
        'Postal Code': ['Postal Code', 'Postal code', 'postalCode', 'zipCode'],
        postalCode: ['postalCode', 'Postal code', 'Postal Code', 'zipCode'],
        zipCode: ['zipCode', 'postalCode', 'Postal code', 'Postal Code'],
        Country: ['Country', 'country'],
        country: ['country', 'Country'],
      }

      return aliases[key] || [key]
    },
    ownerName(purchase) {
      const fields = this.ownerFields(purchase)

      return [this.fieldValue(fields, 'First name'), this.fieldValue(fields, 'Last name')]
        .filter(Boolean)
        .join(' ')
    },
    ownerEditTitle(purchase, index) {
      const item = purchase.orderItem || {}

      return this.adminText('workflowsAll.ticket_name_number_303ffbb', 'Ticket {{name}} #{{number}}', { name: item.name || '', number: index + 1 }).trim()
    },
    ownerFieldLabel(field) {
      const requiredField =
        PERSONAL_FIELD_KEYS.includes(field.key) || (field.validations && field.validations.required)

      return `${this.workflowSchemaFieldLabel(field) || field.key}${requiredField ? '*' : ''}`
    },
    ownerFieldMessages(field) {
      const requiredMessages = {
        'First name': this.adminText('workflowsAll.owner_first_name_is_required_b467815', 'Owner first name is required.'),
        'Last name': this.adminText('workflowsAll.owner_last_name_is_required_95e1d22', 'Owner last name is required.'),
        Email: this.adminText('workflowsAll.owner_email_is_required_242f423', 'Owner email is required.'),
      }

      return {
        ...this.adminFieldValidationMessages(field.errorMessages || {}),
        required: requiredMessages[field.key] || this.adminText('workflowsAll.this_field_is_required_2ab7886', 'This field is required.'),
        email: this.adminText('workflowsAll.enter_a_valid_email_address_e2cf2c9', 'Enter a valid email address.'),
      }
    },
    ownerFieldValidator(index, key) {
      const ownerValidation =
        this.$v.ownerForms &&
        this.$v.ownerForms.$each &&
        this.$v.ownerForms.$each[index] &&
        this.$v.ownerForms.$each[index].fields

      return ownerValidation ? ownerValidation[key] : null
    },
    ownerFieldErrorMessages(index, field) {
      const validator = this.ownerFieldValidator(index, field.key)
      if (!validator || !validator.$dirty) {
        return []
      }
      const messages = this.ownerFieldMessages(field)

      return Object.keys(messages)
        .filter(
          key => Object.prototype.hasOwnProperty.call(validator, key) && validator[key] === false,
        )
        .map(key => messages[key])
    },
    touchOwnerField(index, key) {
      const validator = this.ownerFieldValidator(index, key)
      if (validator) {
        validator.$touch()
      }
    },
    openFirstInvalidOwner() {
      if (!this.$v.ownerForms || !this.$v.ownerForms.$each) {
        return
      }
      const invalidIndex = this.ownerForms.findIndex((_, index) => {
        const rowValidation = this.$v.ownerForms.$each[index]

        return rowValidation && rowValidation.fields && rowValidation.fields.$invalid
      })
      if (invalidIndex !== -1) {
        this.expandedOwnerIndex = invalidIndex
      }
    },
    isBuyerField(key) {
      return BUYER_FIELD_KEYS.includes(key)
    },
    ownerDisplayFields(purchase) {
      const fields = this.ownerFields(purchase)
      const displayKeys = ['Email', 'Document ID', 'Function']
      const availableFields = this.editableOwnerFields.filter(field => {
        return displayKeys.includes(field.key) || (!PERSONAL_FIELD_KEYS.includes(field.key) && fields[field.key])
      })

      return availableFields.map(field => ({
        key: field.key,
        label: field.key === 'Email' ? this.adminText('workflowsAll.field_e_mail', 'E-mail') : field.label || field.key,
        value: this.fieldValue(fields, field.key),
      }))
    },
    toggleOwner(index) {
      this.expandedOwnerIndex = this.expandedOwnerIndex === index ? -1 : index
    },
    startEditingOwners() {
      this.ownerForms = this.orderPurchases.map((purchase, index) => {
        const accreditation = purchase.accreditation
        const fields = { ...(accreditation.fields || {}) }
        this.editableOwnerFields.forEach(field => {
          if (!Object.prototype.hasOwnProperty.call(fields, field.key)) {
            fields[field.key] = ''
          }
        })
        const sameAsBuyer =
          index === 0 &&
          this.fieldValue(fields, 'First name') === this.fieldValue(this.buyerFields, 'First name') &&
          this.fieldValue(fields, 'Last name') === this.fieldValue(this.buyerFields, 'Last name') &&
          this.fieldValue(fields, 'Email') === this.fieldValue(this.buyerFields, 'Email')

        return {
          category: (accreditation.category && accreditation.category.id) || null,
          zones: accreditation.zones || [],
          avatar: accreditation.avatar,
          fields,
          sameAsBuyer,
        }
      })
      this.expandedOwnerIndex = 0
      this.editingOwners = true
      this.$nextTick(() => {
        if (this.$v.ownerForms) {
          this.$v.ownerForms.$reset()
        }
      })
    },
    cancelOwnersEdit() {
      this.editingOwners = false
      this.ownerForms = []
      this.expandedOwnerIndex = 0
      this.$nextTick(() => {
        if (this.$v.ownerForms) {
          this.$v.ownerForms.$reset()
        }
      })
    },
    syncOwnerWithBuyer(index) {
      const ownerForm = this.ownerForms[index]
      if (!ownerForm || !ownerForm.sameAsBuyer) {
        return
      }
      this.$set(ownerForm.fields, 'First name', this.fieldValue(this.buyerFields, 'First name'))
      this.$set(ownerForm.fields, 'Last name', this.fieldValue(this.buyerFields, 'Last name'))
      this.$set(ownerForm.fields, 'Email', this.fieldValue(this.buyerFields, 'Email'))
      BUYER_ADDRESS_FIELD_KEYS.forEach(key => {
        if (!Object.prototype.hasOwnProperty.call(ownerForm.fields, key)) {
          return
        }
        const buyerValue = this.fieldValueAliases(key).reduce((value, alias) => {
          return value || this.fieldValue(this.buyerFields, alias)
        }, '')

        this.$set(ownerForm.fields, key, buyerValue)
      })
    },
    showSnackbar(message, color = 'primary', timeout = DEFAULT_SNACKBAR_TIMEOUT) {
      this.snackbar = {
        open: true,
        message,
        color,
        timeout,
      }
    },
    async withActionLoading(callback) {
      this.actionLoading = true
      try {
        await callback()
      } finally {
        this.actionLoading = false
      }
    },
    async loadAuthorization() {
      try {
        const auth = await getAuthorization(this.eventId, this.organizationSlug)
        this.organizationRole = auth && auth.organizationRole
      } catch (error) {
        this.organizationRole = null
      }
    },
    async resendOrderTickets() {
      await this.withActionLoading(async () => {
        await this.$apollo.mutate({
          mutation: SEND_TICKETS_VIA_EMAIL,
          variables: {
            orderUUID: this.order.uuid,
            eventId: this.eventId,
            lang: this.order.language || 'hr',
            email: this.buyerEmail || null,
          },
        })
        this.showSnackbar('workflowsAll.tickets_sent_successfully_2778e08')
      })
    },
    async resendOwnerTicket(purchase) {
      await this.withActionLoading(async () => {
        await this.$apollo.mutate({
          mutation: RESEND_TICKET_OWNER_EMAIL,
          variables: {
            orderUUID: this.order.uuid,
            eventId: this.eventId,
            purchaseId: purchase.id,
            lang: this.order.language || 'hr',
          },
        })
        this.showSnackbar('workflowsAll.ticket_sent_successfully_309afda')
      })
    },
    ticketDownloadFileName(single = false) {
      const lang = this.order.language || 'hr'
      if (single) {
        return lang === 'en' ? 'CoreEvent-ticket.pdf' : 'CoreEvent-ulaznica.pdf'
      }

      return lang === 'en' ? 'CoreEvent-tickets.pdf' : 'CoreEvent-karte.pdf'
    },
    downloadPdfFromBase64(base64Body, fileName) {
      const byteString = window.atob(base64Body)
      const arrayBuffer = new ArrayBuffer(byteString.length)
      const int8Array = new Uint8Array(arrayBuffer)
      for (let i = 0; i < byteString.length; i++) {
        int8Array[i] = byteString.charCodeAt(i)
      }
      const blob = new Blob([int8Array], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const downloadLink = document.createElement('a')
      downloadLink.href = url
      downloadLink.setAttribute('download', fileName)
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
      URL.revokeObjectURL(url)
    },
    async downloadTickets(purchaseId = null) {
      await this.withActionLoading(async () => {
        const lang = this.order.language || 'hr'
        const purchaseQuery = purchaseId ? `&purchaseId=${purchaseId}` : ''
        const { data: ticket } = await axios.get(
          `${process.env.VUE_APP_SERVER_URL}/files/tickets/${this.order.uuid}?lang=${lang}${purchaseQuery}`,
        )
        this.downloadPdfFromBase64(ticket.body, this.ticketDownloadFileName(!!purchaseId))
      })
    },
    downloadOrderTickets() {
      return this.downloadTickets()
    },
    downloadOwnerTicket(purchase) {
      return this.downloadTickets(purchase.id)
    },
    printAccreditation(accreditationId) {
      const url = `${frontendURL}/organizations/${this.organizationSlug}/events/${this.event.id}/accreditation/${accreditationId}/edit/print`
      window.open(url, '_blank', 'noopener')
    },
    async printAllAccreditations() {
      await this.withActionLoading(async () => {
        await this.$apollo.mutate({
          mutation: BULK_CREATE_PRINT,
          variables: {
            accreditationIds: this.orderPurchases.map(purchase => purchase.accreditation.id),
            id: this.eventId,
          },
        })
        await this.$apollo.queries.getAccreditationOrderDetails.refetch()
        this.showSnackbar('workflowsAll.we_have_sent_you_an_email_with_your_accredita_83f8889')
      })
    },
    async cancelOrder() {
      try {
        await this.withActionLoading(async () => {
          const { data, status } = await axios.post(
            `${process.env.VUE_APP_SERVER_URL}/orders/refund/${this.order.uuid}`,
            null,
            {
              withCredentials: true,
              headers: {
                'X-Requested-With': 'XMLHttpRequest',
              },
            },
          )
          await this.$apollo.queries.getAccreditationOrderDetails.refetch()
          this.isVisibleCancelOrderDialog = false
          // A WSPay transaction older than 90 days cannot be refunded through the API, so the order
          // is refunded here but the money has to be paid out from the WSPay dashboard by hand. The
          // backend explains that in the response message, so it must not be swallowed.
          if (data && data.manualWsPayRefundRequired) {
            this.showSnackbar(data.message, 'warning', ACTION_REQUIRED_SNACKBAR_TIMEOUT)
          } else if (status === 202 || (data && data.provider === 'REVOLUT')) {
            // This exact notice belongs to CoreEvent's pending Revolut refund response.
            const message = status === 202 && data && data.provider === 'REVOLUT' &&
              data.message === 'Refund request was accepted and is awaiting Revolut confirmation.'
              ? 'integrationCorvus.revolutRefundAccepted'
              : (data && data.message) || 'integrationCorvus.refundPending'
            this.showSnackbar(
              message,
              'warning',
              ACTION_REQUIRED_SNACKBAR_TIMEOUT,
            )
          } else {
            this.showSnackbar('workflowsAll.order_cancelled_successfully_5d1b522')
          }
        })
      } catch (error) {
        const message =
          (error.response && error.response.data && error.response.data.message) || 'workflowsAll.unable_to_cancel_order_c6b0d92'
        this.showSnackbar(message, 'error')
      }
    },
    async submitForm() {
      try {
        await submitGuard(this, async () => {
          await this.withActionLoading(async () => {
            await this.updateAttendee(this.form)
            await this.$apollo.queries.getAccreditationOrderDetails.refetch()
            this.showSnackbar('workflowsAll.accreditation_updated_successfully_c1ef348')
          })
        })
      } catch (error) {
        this.showSnackbar('workflowsAll.unable_to_update_accreditation_d38672e', 'error')
      }
    },
    cancelFormChanges() {
      if (!this.initialForm) {
        return
      }

      this.form = this.cloneFormValue(this.initialForm)
      this.formRenderKey += 1
      this.$v.$reset()
    },
    async saveOwnerForms() {
      if (!this.$v.ownerForms) {
        return
      }
      this.$v.ownerForms.$touch()
      if (this.$v.ownerForms.$invalid) {
        this.openFirstInvalidOwner()
        this.showSnackbar('workflowsAll.please_fill_in_all_required_ticket_owner_fiel_05c2f36', 'error')

        return
      }
      try {
        await this.withActionLoading(async () => {
          await Promise.all(
            this.orderPurchases.map((purchase, index) => {
              const ownerForm = this.ownerForms[index]

              return this.updateAttendee(
                {
                  category: ownerForm.category,
                  zones: ownerForm.zones,
                  avatar: ownerForm.avatar,
                  ...ownerForm.fields,
                },
                purchase.accreditation.uuid,
              )
            }),
          )
          await this.$apollo.queries.getAccreditationOrderDetails.refetch()
          this.editingOwners = false
          this.ownerForms = []
          this.expandedOwnerIndex = 0
          this.showSnackbar('workflowsAll.ticket_owners_updated_successfully_8fc6889')
        })
      } catch (error) {
        this.showSnackbar('workflowsAll.unable_to_update_ticket_owners_271a889', 'error')
      }
    },
    async updateAttendee(values, attendeeUUID = this.accreditationUuid) {
      const { category, zones, avatar, ...rest } = values
      const attendee = {
        fields: rest,
        categoryId: category || null,
        zones,
        avatar,
      }

      return this.$apollo.mutate({
        mutation: UPDATE_ATTENDEE,
        variables: {
          eventId: this.eventId,
          attendeeUUID,
          attendee,
        },
      })
    },
    async deleteAttendee(attendeeUUID, eventId) {
      await this.$apollo.mutate({
        mutation: DELETE_ATTENDEE,
        variables: { eventId, attendeeUUID },
        refetchQueries: ['getEventAccreditations'],
      })
      this.goBack()
    },
    hydrateAccreditationDetails(accreditation, event) {
      if (!accreditation || !event) {
        this.order = null
        this.currentAttendee = null
        this.loadError = 'workflowsAll.unable_to_load_accreditation_details_cb8fb00'

        return
      }

      const { status, category, fields, zones, avatar } = accreditation
      this.currentAttendee = accreditation
      this.form = {
        category: (category && category.id) || null,
        zones,
        avatar,
        ...fields,
      }
      this.initialForm = this.cloneFormValue(this.form)
      this.formRenderKey += 1
      this.status = status
      this.event = event
      this.customStyles =
        (this.event.eventUtils &&
          this.event.eventUtils.inviteFormStyles &&
          this.event.eventUtils.inviteFormStyles) ||
        {}
      this.schema = createAttendeeSchema(this.event.attendeeFields, fields)
    },
  },
  apollo: {
    getAccreditationOrderDetails: {
      query: GET_ACCREDITATION_ORDER_DETAILS,
      variables() {
        return {
          eventId: this.eventId,
          attendeeUUID: this.accreditationUuid,
        }
      },
      result({ data, loading, errors }) {
        if (errors) {
          this.loadError = 'workflowsAll.unable_to_load_accreditation_order_details_26a5d30'

          return
        }
        if (!loading) {
          const details = data && data.getAccreditationOrderDetails
          if (!details) {
            this.order = null
            this.hydrateAccreditationDetails(null, null)

            return
          }
          this.hydrateAccreditationDetails(details.attendee, details.event)
          this.order = details.order || null
          this.loadError = ''
        }
      },
    },
  },
}
</script>

<style scoped>
.accreditation-order-page {
  color: #333333;
  min-width: 0;
}

.order-loading {
  padding: 24px;
}

.order-back-button {
  align-items: center;
  background: transparent;
  border: 0;
  color: #0277bd;
  cursor: pointer;
  display: inline-flex;
  font-family: 'Raleway', sans-serif;
  font-size: 20px;
  font-weight: 700;
  gap: 8px;
  margin-bottom: 24px;
  padding: 0;
}

.owner-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.order-card,
.owner-card,
.owner-edit-card {
  background: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 12px;
  overflow: hidden;
}

.card-heading,
.owner-card-header,
.owner-edit-header {
  background: #f5f5f5;
}

.card-heading {
  padding: 16px 24px;
}

.card-heading h1,
.card-heading h2,
.plain-card-heading h2,
.history-header h2,
.section-title-row h2 {
  color: #666666;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.72px;
  line-height: 1.2;
  margin: 0;
}

.card-heading p {
  color: #666666;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 18px;
  line-height: 1.2;
  margin: 2px 0 0;
}

.plain-card-heading {
  padding: 24px 24px 0;
}

.card-body {
  padding: 24px;
}

.detail-row {
  align-items: center;
  border-bottom: 1px solid #d9d9d9;
  display: flex;
  gap: 16px;
  min-width: 0;
  min-height: 48px;
}

.detail-row:last-child {
  border-bottom: 0;
}

.detail-row > span,
.detail-row-action span,
.summary-row span,
.history-row small {
  color: #665f5c;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
}

.detail-row > span {
  width: 112px;
}

.detail-row strong,
.summary-row strong,
.history-row strong {
  color: #666666;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 18px;
  font-weight: 600;
  min-width: 0;
  overflow-wrap: anywhere;
}

.detail-row-action {
  flex-wrap: wrap;
  justify-content: space-between;
}

.detail-row-action > div {
  align-items: center;
  display: flex;
  flex: 1 1 250px;
  gap: 16px;
  min-width: 0;
}

.text-action {
  align-items: center;
  background: transparent;
  border: 0;
  color: #0277bd;
  cursor: pointer;
  display: inline-flex;
  font-family: 'Raleway', sans-serif;
  font-size: 18px;
  font-weight: 700;
  gap: 8px;
  padding: 8px 0;
  text-transform: uppercase;
}

.text-action:disabled {
  cursor: default;
  opacity: 0.45;
}

.section-title-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: space-between;
}

.edit-owners-action {
  font-size: 16px;
}

.owner-card-header,
.owner-edit-header,
.history-header {
  align-items: center;
  border: 0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;
  text-align: left;
  width: 100%;
}

.owner-card-header span {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.owner-card-header strong,
.owner-edit-header strong {
  color: #666666;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.72px;
  line-height: 1.2;
}

.owner-card-header small {
  color: #666666;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 18px;
}

.owner-card-body {
  padding: 16px 24px 32px;
}

.owner-details .detail-row {
  flex-wrap: wrap;
  position: relative;
}

.owner-details .detail-row strong {
  flex: 1 1 180px;
}

.owner-resend {
  margin-left: auto;
}

.owner-actions,
.summary-actions,
.form-actions,
.owner-edit-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.owner-actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 250px), 1fr));
  margin-top: 32px;
}

.owner-actions .pill-button {
  min-width: 0;
  width: 100%;
}

.accreditation-actions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 28px;
}

.accreditation-actions-card {
  background: #f5f5f5;
}

.accreditation-actions .pill-button {
  flex: 0 0 48px;
}

.pill-button-accreditation-print {
  background: #ffffff;
  color: #0277bd;
}

.accreditation-print-icon {
  height: 24px;
  width: 24px;
}

.pill-button {
  align-items: center;
  border: 0;
  border-radius: 24px;
  cursor: pointer;
  display: inline-flex;
  flex: 1 1 0;
  font-family: 'Raleway', sans-serif;
  font-size: 18px;
  font-weight: 700;
  gap: 8px;
  height: 48px;
  justify-content: center;
  padding: 0 24px;
  text-transform: uppercase;
  white-space: nowrap;
}

.pill-button:disabled {
  cursor: default;
  opacity: 0.5;
}

.pill-button-primary {
  background: #0277bd;
  color: #ffffff;
}

.pill-button-outline {
  background: #ffffff;
  border: 2px solid #0277bd;
  color: #0277bd;
}

.pill-button-light {
  background: #ffffff;
  color: #0277bd;
}

.pill-button-alert-outline {
  background: #ffffff;
  border: 2px solid #db384c;
  color: #db384c;
}

.owner-edit-header {
  gap: 24px;
  height: 80px;
}

.owner-edit-header strong {
  flex: 1 1 auto;
  font-weight: 600;
}

.owner-edit-body {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 24px;
}

.same-as-buyer-row {
  align-items: center;
  color: #333333;
  display: flex;
  font-family: 'Raleway', sans-serif;
  font-size: 16px;
  gap: 16px;
}

.same-as-buyer-row input {
  height: 16px;
  width: 16px;
}

.owner-edit-actions,
.form-actions {
  padding-top: 8px;
}

.form-actions {
  justify-content: space-between;
}

.accreditation-form-card {
  padding: 24px;
}

.date-row {
  display: flex;
  gap: 8px;
}

.date-row > * {
  width: 100%;
}

.summary-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.summary-row {
  align-items: flex-start;
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.summary-body hr {
  border: 0;
  border-top: 1px solid #d9d9d9;
  margin: 0;
  width: 100%;
}

.summary-total {
  align-items: baseline;
  display: grid;
  grid-template-columns: 1fr auto;
}

.summary-total span,
.summary-total strong {
  color: #333333;
  font-family: 'Source Sans Pro', sans-serif;
  font-weight: 700;
}

.summary-total span {
  font-size: 16px;
}

.summary-total strong {
  font-size: 24px;
}

.summary-total small {
  color: #979797;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 12px;
  grid-column: 1 / 3;
}

.summary-actions {
  background: #f5f5f5;
  border-top: 1px solid #d9d9d9;
  flex-direction: column;
  padding: 24px;
}

.summary-actions .pill-button {
  flex: 0 0 auto;
  width: 100%;
}

.cancel-order-dialog {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 0 24px rgba(51, 51, 51, 0.2);
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
}

.cancel-order-dialog h2 {
  color: #0277bd;
  font-family: 'Raleway', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
}

.cancel-order-dialog-copy {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cancel-order-dialog-copy p {
  color: #333333;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 18px;
  line-height: 1.5;
  margin: 0;
}

.cancel-order-dialog-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.cancel-order-dialog-button {
  align-items: center;
  border-radius: 24px;
  cursor: pointer;
  display: inline-flex;
  font-family: 'Raleway', sans-serif;
  font-size: 18px;
  font-weight: 700;
  height: 48px;
  justify-content: center;
  min-width: 120px;
  padding: 0 24px;
  text-transform: uppercase;
}

.cancel-order-dialog-button:disabled {
  cursor: default;
  opacity: 0.5;
}

.cancel-order-dialog-button-primary {
  background: #db384c;
  border: 2px solid #db384c;
  color: #ffffff;
}

.cancel-order-dialog-button-secondary {
  background: #ffffff;
  border: 2px solid #d9d9d9;
  color: #666666;
}

.history-header {
  background: #ffffff;
}

.history-list {
  padding: 0 24px 24px;
}

.empty-history {
  color: #979797;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  padding: 8px 0;
}

.history-row {
  align-items: center;
  border-bottom: 1px solid #d9d9d9;
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
}

.history-row:last-child {
  border-bottom: 0;
}

.history-row > span {
  display: flex;
  flex-direction: column;
}

.history-target {
  align-items: center;
  flex-direction: row !important;
  gap: 16px;
  text-align: right;
}

@media (max-width: 800px) {
  .detail-row,
  .detail-row-action,
  .detail-row-action > div,
  .owner-actions,
  .form-actions,
  .owner-edit-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .detail-row > span {
    width: auto;
  }

  .pill-button {
    width: 100%;
  }

  .date-row {
    flex-direction: column;
  }

  .cancel-order-dialog-actions {
    flex-direction: column;
  }

  .cancel-order-dialog-button {
    width: 100%;
  }
}
</style>
